/**
 * BYO design sets panel (Phase 86 slice B, 86-28: R3-8, R3-9, R3-12).
 *
 * For an org that uses its own design IDs, one product's light, dark or
 * medium per colour, pre-selected from each variant's own metafield value
 * (the preview row's `byoSet`, R3-12) and changeable here. The choice is page
 * state (`staging.chooseByoSets`); the run sends it as the scope's
 * `variantSets`, and the plan records the store values it would change.
 * Nothing is written from here.
 *
 * It is the first block of step 2 (Designs), so it has no border of its own.
 * The row writes the product's design ID and metafield letters into staging
 * (`setByoBase`, always mounted), so this panel only reads and chooses.
 *
 * When front and back disagree (`design_sets_differ`), the warning sits at the
 * top with Brett's three one-click answers: "Make all light", "Make all dark"
 * or "Keep as is". It never blocks the product (R3-9). It shows only while a
 * variant it names has no choice yet; each such colour row says what front
 * and back read. After a fix the heading takes focus and a polite live region
 * says what changed; "Undo changes" goes back to the store's values.
 *
 * Grouped per colour: a colour whose sizes read different values shows
 * "Sizes differ" and one line per value until one value is picked for it.
 * Viewers read the values as text (no disabled controls).
 */
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Undo2 } from 'lucide-react';
import { byoDesignSetOf, type DerivedDesignSet } from '@threadheads/shared';
import { Button } from '@/components/ui/button';
import { NoteLine } from '@/components/shared/NoteLine';
import { WarningNotice } from '@/components/shared/WarningNotice';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { ColourDot } from './ColourDot';
import { effectiveByoSets, type StagedDesignsApi } from '../hooks/useStagedDesigns';
import type { PreviewProduct } from '../lib/types';

const LETTERS: readonly DerivedDesignSet[] = ['L', 'D', 'M'];

type Prefill = PreviewProduct['variants'][number]['prefill'];

interface ColourGroup {
  /** The colour as the variants name it, or null for a colour-less product. */
  colour: string | null;
  variantIds: string[];
}

export interface ByoDesignSetsPanelProps {
  product: PreviewProduct;
  staging: StagedDesignsApi;
  admin: boolean;
}

/** A variant's non-empty store values (general, front, back). */
function storeValues(prefill: Prefill): string[] {
  if (!prefill) return [];
  return [prefill.general, prefill.front, prefill.back].filter(
    (value): value is string => typeof value === 'string' && value.trim() !== '',
  );
}

/** True when the variant carries a light / dark value the chosen letter would change. */
function changesStoreValue(prefill: Prefill, chosen: DerivedDesignSet): boolean {
  return storeValues(prefill).some((value) => byoDesignSetOf(value) !== chosen);
}

export function ByoDesignSetsPanel({ product, staging, admin }: ByoDesignSetsPanelProps) {
  const { t } = useTranslation('productSync');
  const productId = product.id;
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [announcement, setAnnouncement] = useState('');

  const metafieldSets = useMemo(() => {
    const out: Record<string, DerivedDesignSet> = {};
    for (const v of product.variants) if (v.byoSet) out[v.id] = v.byoSet;
    return out;
  }, [product.variants]);

  const { chooseByoSets, keepByoWarning, resetByo } = staging;
  const choice = staging.getByo(productId);
  const effective = choice ? effectiveByoSets(choice) : metafieldSets;

  const groups = useMemo<ColourGroup[]>(() => {
    const byKey = new Map<string, ColourGroup>();
    for (const v of product.variants) {
      if (!metafieldSets[v.id]) continue;
      const key = (v.colourName ?? '').toLowerCase();
      const group = byKey.get(key) ?? { colour: v.colourName ?? null, variantIds: [] };
      group.variantIds.push(v.id);
      byKey.set(key, group);
    }
    return [...byKey.values()];
  }, [product.variants, metafieldSets]);

  const variantById = useMemo(() => new Map(product.variants.map((v) => [v.id, v])), [product.variants]);
  const allVariantIds = useMemo(() => groups.flatMap((g) => g.variantIds), [groups]);

  const warning = product.byoDesign?.ok
    ? product.byoDesign.warnings.find((w) => w.warning === 'design_sets_differ')
    : undefined;
  // R3-9: the warning stays only while a variant it names has no choice yet.
  const unresolved = new Set((warning?.variantIds ?? []).filter((id) => choice?.chosen[id] === undefined));
  const showWarning = unresolved.size > 0 && !choice?.warningKept;
  const canUndo = choice !== null && (Object.keys(choice.chosen).length > 0 || choice.warningKept);

  const word = (letter: DerivedDesignSet): string => t(`byo.word.${letter}`);
  /** A store value as a word ("light"), or as the store wrote it when it is not one. */
  const storeWord = (value: string): string => {
    const letter = byoDesignSetOf(value);
    return letter ? word(letter) : value.trim();
  };

  /** Move focus to the heading and say what changed (the buttons may unmount). */
  const settle = (message: string) => {
    setAnnouncement(message);
    headingRef.current?.focus();
  };

  const makeAll = (letter: 'L' | 'D') => {
    chooseByoSets(productId, Object.fromEntries(allVariantIds.map((id) => [id, letter])));
    keepByoWarning(productId);
    settle(letter === 'L' ? t('byo.fix.doneLight') : t('byo.fix.doneDark'));
  };
  const keep = () => {
    keepByoWarning(productId);
    settle(t('byo.fix.doneKeep'));
  };
  const undo = () => {
    resetByo(productId);
    settle('');
  };

  const options = LETTERS.map((letter) => ({ value: letter, label: t(`byo.set.${letter}`) }));
  const headingId = `byo-sets-${productId}`;

  return (
    <section className="space-y-3" aria-labelledby={headingId}>
      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
        <h4 id={headingId} ref={headingRef} tabIndex={-1} className="text-xs font-semibold focus:outline-none">
          {t('byo.panel.title')}
        </h4>
        <span className="text-xs text-muted-foreground">{admin ? t('byo.panel.hint') : t('byo.panel.hintViewer')}</span>
        {admin && canUndo && (
          <Button
            type="button"
            variant="outline"
            className="ml-auto h-8 gap-1.5 border-amber-200 px-3 text-xs text-amber-700 hover:bg-amber-50"
            onClick={undo}
          >
            <Undo2 className="h-3.5 w-3.5" aria-hidden="true" />
            {t('byo.fix.undo')}
          </Button>
        )}
      </div>

      {/* R3-9: a warning, never a block. The two fixes set every variant;
          "Keep as is" leaves each variant on its own value. */}
      {showWarning && (
        <WarningNotice
          actions={
            admin ? (
              <>
                <Button type="button" size="sm" variant="outline" className="h-8 text-xs" onClick={() => makeAll('L')}>
                  {t('byo.fix.makeAllLight')}
                </Button>
                <Button type="button" size="sm" variant="outline" className="h-8 text-xs" onClick={() => makeAll('D')}>
                  {t('byo.fix.makeAllDark')}
                </Button>
                <Button type="button" size="sm" variant="ghost" className="h-8 text-xs" onClick={keep}>
                  {t('byo.fix.keep')}
                </Button>
              </>
            ) : undefined
          }
        >
          <span className="block">{t('byo.warning.design_sets_differ')}</span>
          <span className="mt-0.5 block text-xs">{t('byo.warning.followsBack')}</span>
          <span className="mt-0.5 block text-xs">{admin ? t('byo.fix.scope') : t('byo.warning.viewer')}</span>
        </WarningNotice>
      )}

      <ul className="divide-y rounded-md border">
        {groups.map((group) => {
          const letters = [...new Set(group.variantIds.map((id) => effective[id]).filter((l): l is DerivedDesignSet => !!l))];
          const value: DerivedDesignSet | 'mixed' = letters.length === 1 ? letters[0]! : 'mixed';
          const label = group.colour ?? t('byo.panel.allVariants');

          // The store change this row's choice implies (old -> new).
          const chosenLetters = [
            ...new Set(group.variantIds.map((id) => choice?.chosen[id]).filter((l): l is DerivedDesignSet => !!l)),
          ];
          const updatesStore = group.variantIds.some((id) => {
            const chosen = choice?.chosen[id];
            return chosen !== undefined && changesStoreValue(variantById.get(id)?.prefill, chosen);
          });
          const fromWords = [
            ...new Set(group.variantIds.flatMap((id) => storeValues(variantById.get(id)?.prefill).map(storeWord))),
          ];
          const to = chosenLetters.length === 1 ? word(chosenLetters[0]!) : null;

          // Sizes differ: one line per value, with its sizes.
          const sizesBySet =
            value === 'mixed'
              ? letters.map((letter) => ({
                  letter,
                  sizes: group.variantIds
                    .filter((id) => effective[id] === letter)
                    .map((id) => variantById.get(id)?.sizeName)
                    .filter((s): s is string => !!s),
                }))
              : [];

          // Front and back, on a row the warning still names.
          const sidePairs = showWarning
            ? [
                ...new Set(
                  group.variantIds
                    .filter((id) => unresolved.has(id))
                    .map((id) => {
                      const p = variantById.get(id)?.prefill;
                      const front = p?.front?.trim() || p?.general?.trim() || '';
                      const back = p?.back?.trim() || p?.general?.trim() || '';
                      return `${front ? storeWord(front) : '-'}|${back ? storeWord(back) : '-'}`;
                    }),
                ),
              ]
            : [];

          return (
            <li key={group.colour ?? ''} className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-3 py-2">
              <span className="flex min-w-0 flex-1 basis-40 items-center gap-2 text-xs">
                <ColourDot name={group.colour} className="h-3 w-3" />
                <span className="truncate font-medium" title={label}>
                  {label}
                </span>
                {value === 'mixed' && (
                  <span className="shrink-0 whitespace-nowrap rounded border bg-muted/60 px-1.5 py-0.5 text-[11px] text-muted-foreground">
                    {t('byo.panel.mixed')}
                  </span>
                )}
              </span>
              {admin ? (
                <SegmentedControl<DerivedDesignSet | 'mixed'>
                  options={options}
                  value={value}
                  ariaLabel={
                    value === 'mixed'
                      ? t('byo.panel.setAriaMixed', { colour: label })
                      : t('byo.panel.setAria', { colour: label })
                  }
                  onChange={(letter) => {
                    // The pressed segment is the row's value already: write nothing.
                    if (letter === 'mixed' || letter === value) return;
                    chooseByoSets(productId, Object.fromEntries(group.variantIds.map((id) => [id, letter])));
                  }}
                />
              ) : value !== 'mixed' ? (
                <span className="text-xs font-semibold">{t(`byo.set.${value}`)}</span>
              ) : null}
              {sizesBySet.map(({ letter, sizes }) => (
                <NoteLine key={letter} className="basis-full">
                  {t('byo.panel.mixedLine', { set: t(`byo.set.${letter}`), sizes: sizes.join(', ') })}
                </NoteLine>
              ))}
              {sidePairs.map((pair) => {
                const [front, back] = pair.split('|');
                return (
                  <NoteLine key={pair} className="basis-full" tone="warning" icon={AlertTriangle}>
                    {t('byo.panel.sides', { front, back })}
                  </NoteLine>
                );
              })}
              {updatesStore && to && (
                <NoteLine className="basis-full">
                  {fromWords.length === 1
                    ? t('byo.panel.storeChange', { from: fromWords[0], to })
                    : t('byo.panel.storeChangeMixed', { to })}
                </NoteLine>
              )}
            </li>
          );
        })}
      </ul>

      <span role="status" className="sr-only">
        {announcement}
      </span>
    </section>
  );
}
